import { Typography } from 'antd';
import React, { useEffect } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import {
  Navigate,
  Routes as ReactRouterDomRoutes,
  Route,
} from 'react-router-dom';

import * as Sentry from '@sentry/react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { getDocumentsMetaState } from '../../apps/main/rootReducer';
import Flex from '../../components/Flex';
import GlobalNavigationBar from '../../components/GlobalNavigationBar';
import GlobalSider from '../../components/GlobalSider';
import CustomDragLayer from '../../dnd/components/CustomDragLayer';
import { PrioTheme } from '../../theme/types';
import { makePrioStyles } from '../../theme/utils';
import { officeContext } from '../../util/officeJs';
import Dashboard from '../dashboard/components/Dashboard';
import {
  abortDriveItemFetch,
  DriveItemsFetchContext,
} from '../documents/actions';
import { setDocumentsMetaState } from '../documents/actions/meta';
import DriveItemConflictModal from '../documents/components/Modals/DriveItemConflictModal';
import PreviewModal from '../documents/components/Modals/PreviewModal';
import RemoteFoldersSubModule from '../documents/components/RemoteFoldersSubModule';
import MailMeSubModule from '../mail/components/me/MailMeSubModule';
import MessageCenterSubModule from '../mail/components/MessageCenter/MessageCenterSubModule';
import AmberSearchMePage from '../projects/components/AmberSearchMePage';
import CreateProject from '../projects/components/ProjectCreation/CreateProject';
import ProjectNewsContextProvider from '../projects/components/ProjectNewsContextProvider';
import ProjectsModule from '../projects/components/ProjectsModule';
import ProjectsNavigation from '../projects/components/ProjectsNavigation';
import TimeAndLeaveManagement from '../projects/components/TimeAndLeaveManagement';
import TaskHubModule from '../taskhub/TaskHubModule';
import WidgetArea from '../widgetArea/components/WidgetArea';

const Routes = Sentry.withSentryReactRouterV6Routing(ReactRouterDomRoutes);

const useStyles = makePrioStyles((theme: PrioTheme) => ({
  root: {
    height: '100%',
    display: 'flex',
    flexDirection: 'row',
    overflow: 'hidden',
  },
  //@ts-ignore
  navigation: {
    ...theme.old.components.navigation,
  },
  content: {
    flex: 2,
    overflowX: 'auto',
    overflowY: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    marginBottom: 1,
  },
  fullHeight: {
    height: '100%',
    overflow: 'hidden',
  },
  mainContent: {
    height: '100%',
    overflow: 'hidden',
    position: 'relative',
  },
}));

export const Projects: React.FC<{}> = () => {
  //#region ------------------------------ Defaults
  const classes = useStyles();

  const { t } = useTranslation();

  const isOutsideOfficeContext = !officeContext;

  const dispatch = useDispatch();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const { showDriveItemConflictModalState } = useSelector(
    getDocumentsMetaState
  );
  //#endregion

  //#region ------------------------------ Effects
  useEffect(() => {
    return () => {
      dispatch(
        abortDriveItemFetch(DriveItemsFetchContext.Other, undefined, true)
      );
    };
  }, [dispatch]);

  //#endregion

  return (
    <ProjectNewsContextProvider>
      <DndProvider backend={HTML5Backend}>
        <PreviewModal />
        <DriveItemConflictModal
          visible={
            showDriveItemConflictModalState &&
            showDriveItemConflictModalState !== 'none'
          }
          onClose={() => {
            dispatch(
              setDocumentsMetaState({
                showDriveItemConflictModalState: 'none',
              })
            );
          }}
        />
        <CustomDragLayer />
        <div className={classNames('prio-deprecated-design', classes.root)}>
          <GlobalSider
            title={t('projects:navigation.projects')}
            selectedMainMenuItem={'projects'}
            subMenu={
              <Routes>
                <Route
                  path="*"
                  element={
                    <ProjectsNavigation
                      className={classes.navigation}
                      pathPrefix={''}
                      isSubMenu
                    />
                  }
                />
                <Route
                  path=":projectId"
                  element={
                    <ProjectsNavigation
                      className={classes.navigation}
                      pathPrefix={'../'}
                      isSubMenu
                    />
                  }
                >
                  <Route
                    path=":subModule/*"
                    element={
                      <ProjectsNavigation
                        className={classes.navigation}
                        pathPrefix={'../../'}
                        isSubMenu
                      />
                    }
                  />
                </Route>
              </Routes>
            }
            additionalTriggerBarButton={{
              type: 'link',
              link: '/module/prio/projects/create',
              iconName: 'plus',
              text: t('projects:navigation.newProject'),
            }}
          >
            <Routes>
              <Route
                path="*"
                element={
                  <ProjectsNavigation
                    className={classes.navigation}
                    pathPrefix={''}
                  />
                }
              />
              <Route
                path=":projectId"
                element={
                  <ProjectsNavigation
                    className={classes.navigation}
                    pathPrefix={'../'}
                  />
                }
              >
                <Route
                  path=":subModule/*"
                  element={
                    <ProjectsNavigation
                      className={classes.navigation}
                      pathPrefix={'../../'}
                    />
                  }
                />
              </Route>
            </Routes>
          </GlobalSider>
          <Flex.Column flex={1} className={classes.fullHeight}>
            <GlobalNavigationBar module="projects" />
            <Flex.Row flex={1} className={classes.mainContent}>
              <div className={classes.content}>
                <Routes>
                  <Route
                    path="/"
                    element={
                      <Flex.Column className={classes.fullHeight}>
                        <div
                          className={
                            isOutsideOfficeContext
                              ? 'prio-flex-center-center-navigation-bar'
                              : 'prio-flex-center-center'
                          }
                        >
                          <Typography.Text>
                            {t('projects:noProjectSelected')}
                          </Typography.Text>
                        </div>
                      </Flex.Column>
                    }
                  />
                  <Route path="create/*" element={<CreateProject />} />
                  <Route path="me">
                    <Route path="*" element={<Navigate to="mail" replace />} />
                    <Route path="mail/*" element={<MailMeSubModule />} />
                    <Route path="messageCenter/*">
                      <Route
                        path="*"
                        element={<Navigate to="favorites/mail/inbox" replace />}
                      />
                      <Route
                        path="favorites/mail/*"
                        element={<MessageCenterSubModule pathPrefix={'../'} />}
                      />
                      <Route
                        path=":projectId/mail/*"
                        element={<MessageCenterSubModule pathPrefix={'../'} />}
                      />
                    </Route>
                    <Route path="taskhub/*" element={<TaskHubModule />} />

                    <Route path="timeAndLeaveManagement/*">
                      <Route
                        path="*"
                        element={<Navigate to="summary" replace />}
                      />
                      <Route
                        path=":selectedList/*"
                        element={<TimeAndLeaveManagement pathPrefix={'../'} />}
                      />
                    </Route>
                    <Route
                      path="remoteFolders/*"
                      element={<RemoteFoldersSubModule />}
                    />
                    <Route
                      path="amberSearch/*"
                      element={<AmberSearchMePage />}
                    />
                    <Route path="dashboard/*" element={<Dashboard />} />
                  </Route>
                  <Route path=":projectId/*" element={<ProjectsModule />} />
                </Routes>
              </div>
              <WidgetArea />
            </Flex.Row>
          </Flex.Column>
        </div>
      </DndProvider>
    </ProjectNewsContextProvider>
  );
};

export default Projects;
